// SPDX-License-Identifier: MIT
import * as z from "zod/mini";
import { font, rgba } from '../brand.ts';
import { outCubic } from '../core/ease.ts';
import { TAU, clamp, lerp, smoothstep, window4 } from '../core/math.ts';
import { spring } from '../core/spring.ts';
import { DESIGN_H, DESIGN_W, defineScene } from '../scene.ts';
import { Camera } from '../three.ts';
const MAX_DOTS = 1400;
const targetCache = new Map<string, Array<[
    number,
    number
]>>();
/**
 * Sample target points from the rendered wordmark. Memoised per (text, font): a pure function of
 * props, so caching does not break frame independence.
 */
function sampleWordmark(text: string, fontSpec: string): Array<[
    number,
    number
]> {
    const key = `${fontSpec}|${text}`;
    const cached = targetCache.get(key);
    if (cached)
        return cached;
    const W = 1600;
    const H = 360;
    const off = new OffscreenCanvas(W, H);
    const c = off.getContext('2d', { willReadFrequently: true })!;
    c.font = fontSpec;
    c.textAlign = 'center';
    c.textBaseline = 'middle';
    c.fillStyle = '#fff';
    c.fillText(text, W / 2, H / 2);
    const data = c.getImageData(0, 0, W, H).data;
    const pts: Array<[
        number,
        number
    ]> = [];
    const step = 5;
    for (let y = 0; y < H; y += step)
        for (let x = 0; x < W; x += step)
            if (data[(y * W + x) * 4 + 3]! > 140)
                pts.push([x - W / 2, y - H / 2]);
    // Deterministic thinning to the dot budget.
    const out = pts.length <= MAX_DOTS
        ? pts
        : Array.from({ length: MAX_DOTS }, (_, i) => pts[Math.floor((i * pts.length) / MAX_DOTS)]!);
    targetCache.set(key, out);
    return out;
}
/** ~1400 dots orbit as a sphere, converge into the wordmark, then the solid mark draws in. */
export default defineScene({
    id: 'logo-assemble',
    version: '0.1.0',
    aspects: ['16:9'],
    propsSchema: z.object({
        wordmark: z.optional(z.string().check(z.maxLength(24))),
        tagline: z.optional(z.string().check(z.maxLength(60))),
        url: z.optional(z.string().check(z.maxLength(60))),
    }),
    glyphs: (p) => (p.wordmark ?? '') + (p.tagline ?? '') + (p.url ?? ''),
    motion: (_p, env) => (env.localBeat < 3.5 ? 0.9 : 0.1),
    draw(ctx, props, env) {
        const { colors } = env.brand;
        const b = env.localBeat;
        const cx = DESIGN_W / 2;
        const cy = DESIGN_H / 2 - 60;
        ctx.fillStyle = colors.ink;
        ctx.fillRect(0, 0, DESIGN_W, DESIGN_H);
        const text = props.wordmark ?? env.brand.name;
        const markFont = font(env.brand, 800, 210);
        const targets = sampleWordmark(text, markFont);
        const r = env.rng('dots');
        const cam = new Camera({ fov: 50, position: [0, 0, -1800], yaw: env.local * 0.9 });
        // Converge: each dot springs from its orbit position to its target with a staggered delay.
        const glowOn = smoothstep(2.6, 3.4, b);
        ctx.save();
        ctx.globalCompositeOperation = 'lighter';
        for (const [tx, ty] of targets) {
            const u = r() * 2 - 1;
            const phi = r() * TAU;
            const delay = r() * 1.1;
            const rad = 520 + r() * 120;
            const s = Math.sqrt(1 - u * u);
            const p = cam.project([rad * s * Math.cos(phi), rad * u, rad * s * Math.sin(phi)]);
            const k = clamp(spring((b - 0.6 - delay) * env.spb, { stiffness: 90, damping: 14 }), 0, 1.15);
            const sx = p ? p.x : cx;
            const sy = p ? p.y : cy;
            const x = lerp(sx, cx + tx, k);
            const y = lerp(sy, cy + ty, k);
            const size = lerp(p ? 2.4 + 2.2 * p.scale : 2, 3.2, clamp(k));
            const fade = 1 - 0.85 * smoothstep(3.2, 4.4, b);
            ctx.fillStyle = rgba(k > 0.9 ? colors.paper : colors.accent, (0.55 + 0.45 * clamp(k)) * fade);
            ctx.fillRect(x - size / 2, y - size / 2, size, size);
        }
        ctx.restore();
        // Solid mark: stroke draws on, then the fill lands just before the accent at beat 4.
        ctx.font = markFont;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        const draw = smoothstep(2.8, 3.8, b);
        if (draw > 0) {
            ctx.save();
            ctx.lineWidth = 3;
            ctx.strokeStyle = rgba(colors.paper, 0.9 * (1 - smoothstep(4.2, 5, b)));
            ctx.setLineDash([2400 * draw, 2400]);
            ctx.strokeText(text, cx, cy);
            ctx.restore();
        }
        const fill = smoothstep(3.5, 4.0, b);
        if (fill > 0) {
            ctx.save();
            ctx.fillStyle = colors.paper;
            ctx.globalAlpha = fill;
            ctx.shadowColor = rgba(colors.primary, 0.9 * glowOn);
            ctx.shadowBlur = 50;
            ctx.fillText(text, cx, cy);
            ctx.restore();
            const w = ctx.measureText(text).width;
            env.report?.text('wordmark', [cx - w / 2, cy - 105, w, 210], fill);
        }
        const tail = outCubic(smoothstep(4.4, 5.4, b));
        if (props.tagline && tail > 0) {
            ctx.font = font(env.brand, 500, 44);
            ctx.letterSpacing = '2px';
            ctx.fillStyle = rgba(colors.paper, 0.85 * tail);
            ctx.fillText(props.tagline, cx, cy + 190 + 20 * (1 - tail));
            ctx.letterSpacing = '0px';
            env.report?.text('tagline', [cx - 600, cy + 165, 1200, 50], tail);
        }
        const urlA = window4(5.0, 5.8, 99, 100, b);
        if (props.url && urlA > 0) {
            ctx.font = font(env.brand, 500, 30, 'mono');
            ctx.letterSpacing = '6px';
            ctx.fillStyle = rgba(colors.accent, urlA);
            ctx.fillText(props.url, cx, cy + 270);
            ctx.letterSpacing = '0px';
            env.report?.text('url', [cx - 400, cy + 250, 800, 40], urlA);
        }
        ctx.fillStyle = rgba(colors.primary, 0.08 * glowOn);
        ctx.fillRect(0, DESIGN_H - 4, DESIGN_W, 4);
    },
});
